// MACHINERY V2 (Stroje) — reference lists -> FormSelect options (M2.7).
//
// Spec: data package §10 (option sources) + §13 (which reference tables are
// data-driven). Pure mapping, no fetching: the thunks stay in
// useCustomMachineForm.ts and the slices stay untouched.
//
// >>> WHAT IS AND IS NOT TRANSLATED HERE — the one deliberate call. <<<
// MACHINE TYPE is translated; every other reference list is not. The line is
// drawn where the codebase already draws it: `customerMachinerySlice.ts:272`
// filters the type list with `x.name === "CNC Machine" || x.name ===
// "Production line"`, and `models/models.ts:203-207` declares those two strings
// as the `MachineTypes` constant. The set is CLOSED, two members, and its
// member names are already load-bearing constants in code — which is exactly
// §3's "closed enum -> plain lookup map" case. Artboards 0802-0804 draw "CNC
// stroj" / "Výrobná linka", and data package §0 rule 4 + §14 say a hard-coded
// English label is a gap to FIX, not to copy.
// The OTHER lists (manufacturers, operation types, axis configurations, 13+
// control-system brands, automation levels) are OPEN, DB-driven sets whose rows
// an admin can add; inventing a key map for them would silently drop any row we
// had not anticipated. They render the server's `name`, exactly as v1 does.
// The BINDING is unchanged either way — the option's value is still the row id.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { AxisConfiguration, MachineType, MachineTypes } from "../../models/models";
import { FormSelectOption } from "../forms/FormSelect";
import { Location } from "../../components/locations/locationTypes";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Closed by construction: the slice ships exactly these two rows. Anything
 *  else falls back to the server's own name rather than rendering blank. */
const MACHINE_TYPE_LABEL_KEYS: Record<string, string> = {
  [MachineTypes.cnc]: "myMachinery:v2.add.typeCnc",
  [MachineTypes.productionLine]: "myMachinery:v2.add.typeLine",
};

export const isProductionLine = (types: MachineType[], typeId?: string): boolean =>
  !!typeId && types.find((t) => t.id === typeId)?.name === MachineTypes.productionLine;

export const machineTypeOptions = (
  types: MachineType[],
  translate: Translate
): FormSelectOption[] =>
  types.map((t) => {
    const key = MACHINE_TYPE_LABEL_KEYS[t.name];
    return { id: t.id, label: key ? translate(key) : t.name };
  });

/** Every `{id, name}` reference list — manufacturers, operation types,
 *  control-system brands, automation levels. Value = the row id. */
export const referenceOptions = (rows: { id: string; name: string }[]): FormSelectOption[] =>
  rows.map((r) => ({ id: r.id, label: r.name }));

/**
 * AXIS CONFIGURATION IS THE ODD ONE OUT — ITS VALUE IS THE NAME.
 * Data package §10: "Autocomplete onChange only clearErrors; value captured as
 * option NAME via the register spread; `handleCreateMachine` resolves
 * `axisConfigurations.find(x => x.name === value)?.id`. All other id fields
 * resolve by id." (v1: components/machineTypeForms/CncForm.tsx:152 registers
 * the Autocomplete's TextField; components/MachineDialog.tsx:351 resolves it.)
 * The quirk is preserved deliberately, so this mapper puts the NAME in `id`.
 * `resolveAxisConfigurationId` below is the other half.
 */
export const axisConfigurationOptions = (rows: AxisConfiguration[]): FormSelectOption[] =>
  rows.map((r) => ({ id: r.name, label: r.name }));

export const resolveAxisConfigurationId = (
  rows: AxisConfiguration[],
  value?: string
): string | undefined => rows.find((x) => x.name === value)?.id;

/**
 * Locations, flattened to the SAME shape v1's LocationPicker builds
 * (components/locations/LocationPicker.tsx:43-77): disabled rows dropped, a
 * parent WITH children contributes its children (grouped under the parent name)
 * and is not itself selectable, a childless top-level location is selectable on
 * its own. Rebuilt against FormSelect's `group` field rather than importing the
 * picker — that component is MUI (`Autocomplete`), which §3 keeps out of v2.
 *
 * The machine-count suffix is the §14 i18n gap being FIXED, not copied: v1
 * renders a hard-coded English "machine"/"machines" (LocationPicker.tsx:197).
 */
export const locationOptions = (
  locations: Location[],
  translate: Translate
): FormSelectOption[] => {
  const withCount = (name: string, count?: number): string =>
    typeof count === "number"
      ? `${name} · ${translate("myMachinery:v2.add.machineCount", { count })}`
      : name;

  const out: FormSelectOption[] = [];
  locations.forEach((loc) => {
    if (loc.isDisabled) return;
    if (loc.childLocations && loc.childLocations.length > 0) {
      loc.childLocations.forEach((child) => {
        if (child.isDisabled) return;
        out.push({
          id: child.id,
          label: withCount(child.name, child.machineCount),
          group: loc.name,
        });
      });
      return;
    }
    out.push({ id: loc.id, label: withCount(loc.name, loc.machineCount) });
  });
  return out;
};

/** Control-system MODELS feed a freeSolo text input (artboard 0803 draws an
 *  input, not a select), so they are plain suggestion strings for a datalist. */
export const controlSystemModelNames = (rows: { id: string; name: string }[]): string[] =>
  rows.map((r) => r.name);
